import { Calendar } from '@fullcalendar/core'
import moment from 'moment'
import { buildCalendarOptions } from '../calendar-options'
import { SLOT_ROW_PX } from '../bar-content'
import { GridContextMenu } from './grid-context-menu'
import { SlotZoom } from './slot-zoom'
import { EventFeed } from './event-feed'
import { TimeGestures } from './time-gestures'
import { TouchTravel } from './touch-travel'
import { readSlotBoxes, readColumnBoxes, timeAtPoint } from '../grid-geometry'
import { intervalIdOf } from '../calendar-event-element'
import { NO_DATES, toolbarStateOf } from '../calendar-toolbar'
import { isNarrowScreen } from '../narrow-screen'
import { weekKeeper } from '../week-snapshot'

/* global ResizeObserver */

/** The CSS variable that sets the height of one slot row of the grid. */
const SLOT_ROW = '--schedule-slot-row'

export class CalendarManager {
  constructor (controller, SceneCollectionService) {
    this.controller = controller
    this.calendar = null
    this.sizeObserver = null
    this._refetchTimeout = null
    // The template reads this state. followView replaces it at each change of
    // dates, and when the day changes while the page is open.
    this.toolbar = NO_DATES
    // The interval of the Preflight row that the operator points at. barClasses
    // reads it, so a new render keeps the mark.
    this.highlightedIntervalId = null
    this.eventFeed = new EventFeed(this, SceneCollectionService)
    this.touchTravel = new TouchTravel()
    this.timeGestures = new TimeGestures(this, this.eventFeed, SceneCollectionService, this.touchTravel)
    this.slotZoom = new SlotZoom(this)
    this.contextMenu = new GridContextMenu(this)
    this._keepWeek = weekKeeper()
  }

  /**
   * The snapshot of the intervals and the dates that the grid shows. Every
   * reader of the week takes this snapshot, so the readers cannot disagree.
   *
   * @returns {Object} See weekFrom in week-snapshot.js
   */
  week () {
    return this._keepWeek(this.calendar)
  }

  /**
   * The ids of the playlists of the scene. The template gives them to the
   * panel. The value is null until the first fetch completes.
   */
  get sceneCollectionIds () {
    return this.eventFeed.sceneCollectionIds
  }

  /**
   * Subscribe to the updates of the scene, and draw the calendar.
   *
   * The controller calls this one time, from $postLink.
   *
   * @param {HTMLElement} calendarEl - The container element for the calendar
   */
  create (calendarEl) {
    this.eventFeed.setupWebSocketSubscriptions()
    this.initializeCalendar(calendarEl)
  }

  /**
   * Add a class to the bars of one interval. Remove it from all other bars.
   *
   * eventClassNames sets the class at the next render. But the selection and
   * the Preflight mark are not part of the state that FullCalendar renders
   * from. Nothing repaints before the next fetch. This method moves the class
   * in the DOM immediately. The move also cleans up. The class first goes off
   * the bars that had it.
   *
   * @param {string} className
   * @param {number|string|null} intervalId - Null removes the class from all bars
   */
  applyIntervalClass (className, intervalId) {
    if (!this.calendar || !this.calendar.el) return

    const wanted = (intervalId === null || intervalId === undefined)
      ? null
      : String(intervalId)

    Array.from(this.calendar.el.querySelectorAll('.fc-event')).forEach(el => {
      el.classList.toggle(className, wanted !== null && intervalIdOf(el) === wanted)
    })
  }

  /**
   * Show the selection in the grid and on the on-screen surfaces.
   *
   * The bars of the selected interval get an outline. The surfaces are drawn
   * again, because they mark the stretches that the selected interval wins. The
   * surfaces read the selection during the render, so a redraw is their full
   * update.
   */
  markInspected () {
    const inspecting = this.controller.stateManager && this.controller.stateManager.inspecting

    this.applyIntervalClass('fc-event-inspecting', inspecting ? inspecting.intervalId : null)
    this.renderOnScreenSurfaces()
  }

  /**
   * Outline the interval of a preflight row, and no other interval.
   *
   * An interval outside the week on show has no bar. Then no outline is drawn.
   * The row gives the days of that interval, and the operator navigates there.
   *
   * @param {number|null} intervalId - Null removes the outline
   */
  highlightInterval (intervalId) {
    this.highlightedIntervalId = intervalId
    this.applyIntervalClass('fc-event-preflight-target', intervalId)
  }

  /**
   * Go to the day of a moment, and scroll the grid to its time.
   *
   * @param {Date|string|Object} time
   */
  showTime (time) {
    if (!this.calendar) {
      return
    }

    const at = moment(time)

    this.calendar.gotoDate(at.toDate())
    this.calendar.scrollToTime(at.format('HH:mm:ss'))
  }

  /**
   * Read the toolbar state from the view that the grid shows now.
   *
   * datesSet and the now-line hooks call this method. FullCalendar calls them
   * outside Angular, so the page needs a digest.
   *
   * @param {Object} view - The view that the hook gives
   */
  followView (view) {
    this.toolbar = toolbarStateOf(view, new Date())
    this.controller._$scope.$applyAsync()
  }

  /** Show the previous week or day. The buttons of the toolbar call these four methods. */
  showPrevious () {
    if (this.calendar) this.calendar.prev()
  }

  showNext () {
    if (this.calendar) this.calendar.next()
  }

  showToday () {
    if (this.calendar) this.calendar.today()
  }

  /** @param {string} viewType - timeGridWeek or timeGridDay */
  showView (viewType) {
    if (this.calendar) this.calendar.changeView(viewType)
  }

  /**
   * Give the range that the grid shows, or null before the calendar exists.
   *
   * @returns {Object|null} { start, end } as Dates. The end is exclusive.
   */
  visibleRange () {
    if (!this.calendar || !this.calendar.view) {
      return null
    }

    return { start: this.calendar.view.activeStart, end: this.calendar.view.activeEnd }
  }

  /**
   * Tell if the grid must go to another range to show a moment.
   *
   * A save from the panel can write an interval in another week. Without a
   * move, the panel closes and the grid shows no new bar. A moment in the
   * range on show needs no move, so the view of the operator stays. Without a
   * grid or a valid moment, no move is possible.
   *
   * @param {Date|string|Object} time
   * @returns {boolean}
   */
  mustMoveToShow (time) {
    const range = this.visibleRange()
    const at = moment(time)

    return Boolean(range) && at.isValid() && (at.isBefore(range.start) || !at.isBefore(range.end))
  }

  /**
   * Find the time that the grid shows at a point on the screen.
   *
   * The DOM measurement is here, because this class controls the calendar
   * element. The arithmetic is in grid-geometry, where a test can do it without
   * a layout engine. Both coordinate systems are the viewport. Give the clientX
   * and the clientY of a mouse event directly to this method.
   *
   * @param {number} clientX
   * @param {number} clientY
   * @returns {Date|null} Null at a point beside the day columns, or with no grid
   */
  timeAtPoint (clientX, clientY) {
    if (!this.calendar || !this.calendar.el) {
      return null
    }

    return timeAtPoint(
      readSlotBoxes(this.calendar.el),
      readColumnBoxes(this.calendar.el),
      { x: clientX, y: clientY })
  }

  /**
   * Watch the size of the calendar element.
   *
   * FullCalendar 6 has no observer of its own. It measures again only after a
   * window resize. The panel changes the width of the grid with no window
   * resize. One observer sees both changes, and the first size of the element.
   */
  observeSize () {
    this.sizeObserver = new ResizeObserver(() => this.handleResize())
    this.sizeObserver.observe(this.calendar.el)
  }

  /** Measure the grid again, and then draw the on-screen surfaces on the new columns. */
  handleResize () {
    if (!this.calendar) return

    this.calendar.updateSize()
    this.redrawAfterRender()
  }

  /**
   * Draw the on-screen surfaces in a later task, with no digest.
   *
   * FullCalendar calls datesSet in its first render pass. Its measuring passes
   * run after that in the same task, so the timeout comes after all passes.
   * The strip can also change the height of the observed element. A change in
   * the observer callback causes a loop error in the browser.
   */
  redrawAfterRender () {
    this.controller._$timeout(() => this.renderOnScreenSurfaces(), 0, false)
  }

  /**
   * Draw both on-screen surfaces again. These are the strip below the grid and
   * the ribbon in the grid.
   *
   * The same changes cause a redraw of both surfaces. One method for both
   * prevents a hook that draws only one of them. Two surfaces with different
   * data on the same screen would be a contradiction.
   *
   * There are five causes, and each has one caller:
   * - A new size of the element moves the columns. handleResize calls.
   * - New dates replace the columns and their lanes. datesSet calls.
   * - New events change the result. eventsSet calls.
   * - A zoom step moves the rows. The frame of SlotZoom calls.
   * - A new selection changes the marks. markInspected calls.
   */
  renderOnScreenSurfaces () {
    if (this.controller.onScreenBand) {
      this.controller.onScreenBand.render()
    }

    if (this.controller.onScreenRibbon) {
      this.controller.onScreenRibbon.render()
    }
  }

  destroy () {
    this.controller._$log.info('[ScheduleCalendar] CalendarManager destroying')

    // Do this before the calendar goes. The listeners are on the calendar element.
    this.contextMenu.detach()
    this.touchTravel.detach()

    // The strip is outside the calendar element, so it needs its own removal.
    // The ribbon is inside the element, and it goes with the calendar.
    if (this.controller.onScreenBand) {
      this.controller.onScreenBand.destroy()
    }

    this.slotZoom.destroy()

    if (this._refetchTimeout) {
      this.controller._$timeout.cancel(this._refetchTimeout)
      this._refetchTimeout = null
    }

    if (this.sizeObserver) {
      this.sizeObserver.disconnect()
      this.sizeObserver = null
    }

    this.eventFeed.destroy()

    if (this.calendar) {
      this.calendar.destroy()
      this.calendar = null
    }
  }

  /**
   * Join refetch requests into one. A save starts one refetch immediately. The
   * WebSocket update starts a second one approximately 100 ms later.
   *
   * The method is here and not in the event feed. The feed, a gesture and a
   * save in the panel all ask for a refetch, and the refetch acts on the
   * calendar that this class owns.
   */
  scheduleRefetch (delay = 150) {
    if (this._refetchTimeout) {
      this.controller._$timeout.cancel(this._refetchTimeout)
      this._refetchTimeout = null
    }

    this._refetchTimeout = this.controller._$timeout(() => {
      this._refetchTimeout = null
      if (this.calendar) {
        this.calendar.refetchEvents()
      }
    }, delay)
  }

  /**
   * Give the time that the grid scrolls to when it opens.
   *
   * The grid always opens on today. The scroll starts one hour before the
   * current hour, so the now line is in view below one hour of context. The
   * festival plays at night, so a night hour gets the same rule. FullCalendar
   * uses this time again after a move to another week.
   */
  calculateScrollTime () {
    const hourBefore = Math.max(0, new Date().getHours() - 1)

    return `${String(hourBefore).padStart(2, '0')}:00:00`
  }

  /**
   * Create and render the FullCalendar instance.
   * @param {HTMLElement} calendarEl - The container element for the calendar
   */
  initializeCalendar (calendarEl) {
    this.controller._$log.info('[ScheduleCalendar] Initializing calendar')

    try {
      calendarEl.style.setProperty(SLOT_ROW, `${SLOT_ROW_PX}px`)
      this.calendar = new Calendar(calendarEl, buildCalendarOptions(this, isNarrowScreen(calendarEl)))

      this.calendar.render()
      this.contextMenu.attach()
      this.touchTravel.attach(this.calendar.el)
      this.observeSize()
      this.controller._$log.info('[ScheduleCalendar] Calendar rendered successfully')
    } catch (error) {
      this.controller._$log.error('[ScheduleCalendar] Failed to initialize calendar:', error)
    }
  }

  /**
   * Open the inspector for the interval of the bar that was clicked.
   * @param {Object} info - The event click info from FullCalendar
   */
  handleEventClick (info) {
    this.controller._$log.info('[ScheduleCalendar] Event clicked:', info.event.title)
    this.controller.inspect(info.event)
  }

  /**
   * Open the create panel for a time range that the operator selected.
   * @param {Object} info - The selection info from FullCalendar, with the times
   */
  handleTimeSelection (info) {
    this.controller._$log.info('[ScheduleCalendar] Time range selected:', {
      start: info.startStr,
      end: info.endStr,
      duration: moment(info.end).diff(moment(info.start), 'minutes') + ' minutes'
    })

    // The block stays on the grid. FullCalendar draws it while the operator
    // drags. A clear here removed the block at the moment the panel opened.
    // The operator then looked at the panel and did not see the block go. The
    // panel describes a block that stays visible. The state manager clears the
    // block when the panel shows something else.
    this.controller.inspectNew(info.start, info.end)
  }

  /**
   * Remove the block a drag left on the grid.
   *
   * Called when the panel stops describing that block, which is when it closes
   * or when it opens on something else. FullCalendar ignores this when there is
   * no selection.
   */
  clearTimeSelection () {
    if (this.calendar) {
      this.calendar.unselect()
    }
  }
}
